import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { forgotPassword } from '../../service/AuthService';

type View = 'login' | 'forgot';

export interface LoginModalProps {
    onClose: () => void;
}

const LoginModal = ({ onClose }: LoginModalProps) => {
    const [view, setView] = useState<View>('login');

    // Login state
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [scaCode, setScaCode] = useState('');
    const [showSca, setShowSca] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Forgot password state
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotError, setForgotError] = useState('');
    const [forgotSent, setForgotSent] = useState(false);

    const navigate = useNavigate();
    const { signIn } = useAuthContext();

    const buttonLoginDisabled = !email || !password || loading;
    const buttonScaDisabled = !email || !password || loading || !scaCode;

    const handleScaCancel = () => {
        setShowSca(false);
        setScaCode('');
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setShowSca(false);
        setScaCode('');
        try {
            await signIn({ email, password, scaCode, rememberMe });
            onClose();
            navigate('/dashboard');
        } catch (err) {
            if (err instanceof Error) {
                if (err.message === 'timeout') {
                    setError('Server unavailable, please try again.');
                    setScaCode('');
                } else if (err.message === 'sca_is_required') {
                    setShowSca(true);
                } else if (err.message === 'invalid_credentials') {
                    setScaCode('');
                    setError('Login failed. Please check your credentials.');
                } else {
                    setScaCode('');
                    setError('An unexpected error occurred. Please try again later.');
                }
            } else {
                setScaCode('');
                console.error('Login error:', err);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleForgotSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!forgotEmail) return;
        setForgotLoading(true);
        setForgotError('');
        try {
            await forgotPassword(forgotEmail);
            setForgotSent(true);
        } catch {
            setForgotError('Something went wrong. Please try again.');
        } finally {
            setForgotLoading(false);
        }
    };

    const switchToForgot = () => {
        setError('');
        setForgotEmail(email); // pre-fill with login email if already typed
        setForgotSent(false);
        setForgotError('');
        setView('forgot');
    };

    const switchToLogin = () => {
        setForgotError('');
        setForgotSent(false);
        setView('login');
    };

    const inputCls = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            {loading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="loader border-t-4 border-white rounded-full w-12 h-12 animate-spin"></div>
                </div>
            )}

            <div className={`bg-white rounded-2xl shadow-xl p-8 w-full max-w-md relative ${loading ? 'opacity-50' : ''}`}>
                <button
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
                    onClick={onClose}
                    disabled={loading || forgotLoading}
                >
                    <i className="fas fa-times text-lg"></i>
                </button>

                {/* ── LOGIN VIEW ── */}
                {view === 'login' && !showSca && (
                    <div>
                        <h2 className="text-2xl font-bold mb-6 text-gray-800">Login to Dashboard</h2>
                        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
                        <div className="mb-4">
                            <label className="block text-gray-700 font-medium mb-1 text-sm">Email</label>
                            <input
                                type="email"
                                className={inputCls}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                                disabled={loading}
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-700 font-medium mb-1 text-sm">Password</label>
                            <input
                                type="password"
                                className={inputCls}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                disabled={loading}
                            />
                        </div>
                        <div className="mb-6 flex items-center">
                            <input
                                type="checkbox"
                                id="rememberMe"
                                className="mr-2"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                disabled={loading}
                            />
                            <label htmlFor="rememberMe" className="text-gray-700 text-sm">Remember Me</label>
                        </div>
                        <button
                            type="submit"
                            className={`gradient-bg text-white px-6 py-3 rounded-lg font-medium transition w-full mb-4 hover:opacity-90 ${buttonLoginDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={handleLogin}
                            disabled={buttonLoginDisabled}
                        >
                            {loading ? 'Logging in...' : 'Login'}
                        </button>
                        <div className="text-center">
                            <button
                                type="button"
                                onClick={switchToForgot}
                                className="text-blue-600 hover:underline text-sm"
                            >
                                Forgot password?
                            </button>
                        </div>
                    </div>
                )}

                {/* ── SCA VIEW ── */}
                {view === 'login' && showSca && (
                    <div>
                        <h2 className="text-2xl font-bold mb-6 text-gray-800">Two-Factor Auth</h2>
                        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
                        <div className="mb-6">
                            <label className="block text-gray-700 font-medium mb-1 text-sm">Authenticator code</label>
                            <input
                                type="number"
                                className={inputCls}
                                value={scaCode}
                                onChange={(e) => setScaCode(e.target.value)}
                                disabled={loading}
                            />
                        </div>
                        <button
                            type="submit"
                            className={`gradient-bg text-white px-6 py-3 rounded-lg font-medium transition w-full mb-4 hover:opacity-90 ${buttonScaDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={handleLogin}
                            disabled={buttonScaDisabled}
                        >
                            {loading ? 'Sending...' : 'Send'}
                        </button>
                        <div className="text-center">
                            <button type="button" onClick={handleScaCancel} className="text-blue-600 hover:underline text-sm">
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* ── FORGOT PASSWORD VIEW ── */}
                {view === 'forgot' && (
                    <div>
                        <h2 className="text-2xl font-bold mb-2 text-gray-800">Forgot password?</h2>

                        {!forgotSent ? (
                            <>
                                <p className="text-gray-500 text-sm mb-6">
                                    Enter your email and we'll send you a link to reset your password.
                                </p>
                                {forgotError && <p className="text-red-500 text-sm mb-4">{forgotError}</p>}
                                <div className="mb-6">
                                    <label className="block text-gray-700 font-medium mb-1 text-sm">Email</label>
                                    <input
                                        type="email"
                                        className={inputCls}
                                        value={forgotEmail}
                                        onChange={(e) => setForgotEmail(e.target.value)}
                                        autoComplete="email"
                                        disabled={forgotLoading}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={handleForgotSubmit}
                                    disabled={!forgotEmail || forgotLoading}
                                    className={`gradient-bg text-white px-6 py-3 rounded-lg font-medium transition w-full mb-4 hover:opacity-90 ${(!forgotEmail || forgotLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                                </button>
                            </>
                        ) : (
                            <div className="text-center py-4">
                                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <i className="fas fa-envelope-open-text text-green-500 text-2xl"></i>
                                </div>
                                <p className="text-gray-700 text-sm mb-6">
                                    If an account exists for <strong>{forgotEmail}</strong>, you'll receive a reset link shortly. Check your inbox.
                                </p>
                            </div>
                        )}

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={switchToLogin}
                                className="text-blue-600 hover:underline text-sm"
                            >
                                ← Back to login
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LoginModal;
